import { Field, Label } from '@headlessui/react';
import React, { FC, RefObject, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  AddContributorIcon,
  AddDocumentIcon,
  ArrowIcon,
  ChevronDownIcon,
  CloseCircleIconSmall,
  CrossIcon,
  EditIcon,
  EyeIconSmall,
  GrantAccessIcon,
  RevokeAccessIcon,
  SendInviteIcon,
  SuccessCircleIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import { Actions } from '../../../components/actions/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../components/button/index';
import { Checkbox } from '../../../components/checkbox/index';
import { FormattedDateDistance } from '../../../components/formatted-date-distance/index';
import { RowPayload, TableRow } from '../../../components/table/index';
import { Tag, TagVariant } from '../../../components/tag/index';
import { Cursor, Tooltip } from '../../../components/tooltip/index';
import { useLocale, useModalState } from '../../../hooks/index';
import { downloadDocumentStorage } from '../../../storage/modals/index';
import { stakeholderTypeTranslation } from '../../../translations/stakeholder-translation.util';
import {
  Stakeholder,
  stakeholderExTypeTitle,
  StakeholderRole,
  StakeholderType,
} from '../../../types/stakeholder.types';
import { formatDisplayValue } from '../../../utils/format-display-value.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { toUpperCaseWords } from '../../../utils/to-uppercase-util.util';
import { InviteSubEmailsModal } from '../modals/invite-sub-emails-modal';
import { getStakeholderCommonActions } from '../stakeholder-permissions.const';
import StakeholderStatus from '../stakeholder-status';
import { Column, columnOrderMap } from './stakeholder-table.types';

export type StakeholderTableItemProps = {
  accountId: string;
  className?: string;
  handleCancelInvitation: (id: string, subEmail?: string) => void;
  handleDelete: (id: string, subEmail?: string, hasSubEmails?: boolean) => void;
  handleEdit: (id: string) => void;
  handleEditTermination: (id: string) => void;
  handleGrantAccess: (id: string, hasSubEmails?: boolean) => void;
  handleMakeContributor: (id: string, hasSubEmails?: boolean) => void;
  handleRemoveContributorRole: (id: string, hasSubEmails?: boolean) => void;
  handleResendInvitation: (id: string, subEmail?: string) => void;
  handleRevokeAccess: (id: string, hasSubEmails?: boolean) => void;
  handleSendInvitation: (id: string, subEmail?: string) => void;
  handleTerminate: (id: string) => void;
  handleView: (id: string) => void;
  isSelected: (stakeholderId: string) => boolean;
  onRemoveSelect: (stakeholderId: string) => void;
  onSelect: (stakeholderId: string) => void;
  roleInCompany?: StakeholderRole;
  stakeholder: Pick<
    Stakeholder,
    | 'email'
    | 'filesLinks'
    | 'fullName'
    | 'hasPendingSignatures'
    | 'hasShares'
    | 'id'
    | 'invitations'
    | 'isAccessRevoked'
    | 'isActive'
    | 'isInvited'
    | 'isTerminated'
    | 'lastActive'
    | 'lastVestingDate'
    | 'role'
    | 'subEmails'
    | 'terminationCause'
    | 'terminationDate'
    | 'type'
    | 'users'
    | 'votingRightsPercentage'
  >;
  tableRef?: RefObject<HTMLTableElement | null>;
};

const StakeholderTableRow: FC<StakeholderTableItemProps> = ({
  stakeholder: {
    role,
    type,
    isAccessRevoked,
    id,
    fullName,
    lastActive,
    email,
    votingRightsPercentage,
    hasShares,
    hasPendingSignatures,
    isTerminated,
    filesLinks,
    subEmails,
    users,
    isActive,
    invitations,
  },
  className,
  handleDelete,
  handleEdit,
  handleView,
  handleCancelInvitation,
  handleGrantAccess,
  handleMakeContributor,
  handleRemoveContributorRole,
  handleResendInvitation,
  handleRevokeAccess,
  handleTerminate,
  handleEditTermination,
  handleSendInvitation,
  isSelected,
  onRemoveSelect,
  onSelect,
  roleInCompany,
  accountId,
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const { messagesLocale } = useLocale();
  const isInvitedMainUser = invitations?.[email];

  const {
    canDelete,
    canTerminate,
    canEditTerminate,
    cancelInvitation,
    grantAccess,
    makeContributor,
    resendInvitation,
    revokeAccess,
    revokeContributor,
  } = getStakeholderCommonActions({
    role,
    isInvited: isInvitedMainUser,
    isActive,
    isAccessRevoked,
    isTerminated,
  });
  const openDownloadDocumentModal = downloadDocumentStorage(({ open }) => open);

  const payload: RowPayload<Column> = {
    [Column.CHECK_BOX]: (
      <div className="flex h-full w-full cursor-pointer items-center justify-center">
        <Field className="flex items-center">
          <Checkbox
            checked={isSelected(id)}
            id={id}
            onChange={(checked) => {
              checked ? onSelect(id) : onRemoveSelect(id);
            }}
          />
          <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
        </Field>
      </div>
    ),
    [Column.FULL_NAME]: (
      <Button
        className={twMerge(
          'flex h-full w-full items-center justify-start gap-2 transition-all',
          subEmails?.length ? 'cursor-pointer' : 'cursor-auto',
        )}
        disabled={!subEmails?.length}
        disabledStyle={ButtonDisableStyle.NONE}
        onClick={() => setExpanded((prev) => !prev)}
        styleType="NONE"
      >
        {Boolean(subEmails?.length) && (
          <ChevronDownIcon
            className={twMerge('transition-all', isExpanded ? 'rotate-180' : '-rotate-90')}
          />
        )}
        <Tooltip content={fullName} cursor={Cursor.POINTER}>
          {fullName}
        </Tooltip>
        {accountId === id && (
          <span className="text-sm font-[450] text-gray-700">
            (<AppFormattedMessage id="YOU" />)
          </span>
        )}
      </Button>
    ),
    [Column.TYPE]: isTerminated ? (
      toUpperCaseWords(stakeholderExTypeTitle[type])
    ) : (
      <AppFormattedMessage id={stakeholderTypeTranslation[type]} />
    ),
    [Column.EMAIL]: <Tooltip content={email}>{email}</Tooltip>,
    [Column.DOCUMENTS]: filesLinks.length ? (
      <Tooltip
        content={
          <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
            {filesLinks.length === 1
              ? getS3FileOriginalName(filesLinks[0])
              : `${filesLinks.length} files attached to event`}
          </span>
        }
      >
        <AddDocumentIcon
          className="size-6 cursor-pointer p-[2px]"
          onClick={() => openDownloadDocumentModal(filesLinks)}
        />
      </Tooltip>
    ) : (
      '-'
    ),
    [Column.VOTING_RIGHTS]: formatDisplayValue(votingRightsPercentage, {
      multiply: 100,
    }),
    [Column.LAST_ACTIVE]: isActive ? <FormattedDateDistance date={new Date(lastActive)} /> : '-',
    [Column.STATUS]: (
      <div className="flex items-center gap-3">
        <Tag
          title={isActive ? messagesLocale['ACTIVE'] : messagesLocale['INACTIVE']}
          variant={isActive ? TagVariant.GREEN : TagVariant.YELLOW}
        />
        {isActive && <StakeholderStatus type={role} />}
        {!isInvitedMainUser && !isActive && !isAccessRevoked && (
          <Tag
            callback={() =>
              subEmails?.length
                ? handleOpenCancelInvitationModal()
                : handleSendInvitation(id, email)
            }
            className="cursor-pointer flex-row-reverse"
            data-permission-required
            icon={<ArrowIcon className={'rotate-180'} iconColor="#344054" />}
            title={messagesLocale['INVITE_USER']}
            variant={TagVariant.WHITE}
          />
        )}
        {isInvitedMainUser && !isActive && !isAccessRevoked && (
          <Tag
            icon={<SuccessCircleIcon />}
            title={messagesLocale['INVITED']}
            variant={TagVariant.GRAY}
          />
        )}
        {isAccessRevoked && (
          <Tag title={messagesLocale['ACCESS_REVOKED']} variant={TagVariant.RED} />
        )}
      </div>
    ),
    [Column.ACTION]: (
      <Actions
        actions={[
          {
            callback: () => handleView(id),
            title: messagesLocale['VIEW_DETAILS'],
            icon: <EyeIconSmall />,
          },
          (role !== StakeholderRole.ADMIN || roleInCompany === StakeholderRole.ADMIN) &&
          !isTerminated
            ? {
                callback: () => handleEdit(id),
                title: messagesLocale['EDIT_DETAILS'],
                icon: <EditIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,
          resendInvitation
            ? {
                callback: () => handleResendInvitation(id, email),
                title: messagesLocale['RESEND_INVITATION'],
                icon: <SendInviteIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          cancelInvitation
            ? {
                callback: () => handleCancelInvitation(id, email),
                title: messagesLocale['CANCEL_INVITATION'],
                icon: <CloseCircleIconSmall />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          grantAccess
            ? {
                callback: () => handleGrantAccess(id, Boolean(subEmails?.length)),
                title: messagesLocale['GRANT_ACCESS'],
                icon: <GrantAccessIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          makeContributor
            ? {
                callback: () => handleMakeContributor(id, Boolean(subEmails?.length)),
                title: messagesLocale['MAKE_CONTRIBUTOR'],
                icon: <AddContributorIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          revokeAccess && accountId !== id && !isAccessRevoked
            ? {
                callback: () => handleRevokeAccess(id, Boolean(subEmails?.length)),
                title: messagesLocale['REVOKE_ACCESS'],
                icon: <RevokeAccessIcon iconColor={hasPendingSignatures ? '#D0D5DD' : undefined} />,
                disabled: Boolean(hasPendingSignatures),
                tooltipContent: hasPendingSignatures ? (
                  <AppFormattedMessage id="REVOKE_ACCESS_PENDING_SIGNATURES_TOOLTIP" />
                ) : undefined,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          revokeContributor && accountId !== id
            ? {
                callback: () => handleRemoveContributorRole(id, Boolean(subEmails?.length)),
                title: messagesLocale['REVOKE_CONTRIBUTOR_ROLE'],
                icon: <RevokeAccessIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          canTerminate &&
          !isTerminated &&
          accountId !== id &&
          hasShares &&
          [StakeholderType.ADVISOR, StakeholderType.EMPLOYEE, StakeholderType.FOUNDER].includes(
            type,
          )
            ? {
                callback: () => handleTerminate(id),
                title: messagesLocale['TERMINATE'],
                className: 'text-fireside-500',
                icon: <CrossIcon iconColor="#F04438" />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          canEditTerminate &&
          isTerminated &&
          [StakeholderType.ADVISOR, StakeholderType.EMPLOYEE, StakeholderType.FOUNDER].includes(
            type,
          )
            ? {
                callback: () => handleEditTermination(id),
                title: messagesLocale['EDIT_TERMINATION'],
                icon: <EditIcon />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,

          canDelete && accountId !== id && !hasShares
            ? {
                callback: () => handleDelete(id, undefined, Boolean(subEmails?.length)),
                title: messagesLocale['DELETE'],
                className: 'text-fireside-500',
                icon: <TrashBinIcon iconColor="#F04438" />,
                attributes: {
                  'data-permission-required': true,
                },
              }
            : null,
        ]}
      />
    ),
    id,
  };

  const subEmailsPayload = subEmails?.map((subEmail) => {
    const subUserActive =
      Boolean(users?.find(({ email }) => email === subEmail)) && !isAccessRevoked;

    const isInvited = invitations?.[subEmail];

    return {
      id: subEmail,
      [Column.FULL_NAME]: null,
      [Column.CHECK_BOX]: null,
      [Column.DOCUMENTS]: '-',
      [Column.LAST_ACTIVE]: '-',
      [Column.TYPE]: null,
      [Column.VOTING_RIGHTS]: '-',
      [Column.EMAIL]: <Tooltip content={subEmail}>{subEmail}</Tooltip>,
      [Column.STATUS]: (
        <div className="flex items-center gap-3">
          <Tag
            title={subUserActive ? messagesLocale['ACTIVE'] : messagesLocale['INACTIVE']}
            variant={subUserActive ? TagVariant.GREEN : TagVariant.YELLOW}
          />
          {!isInvited && !subUserActive && !isAccessRevoked && (
            <Tag
              callback={() => handleSendInvitation(id, subEmail)}
              className="cursor-pointer flex-row-reverse"
              data-permission-required
              icon={<ArrowIcon className={'rotate-180'} iconColor="#344054" />}
              title={messagesLocale['INVITE_USER']}
              variant={TagVariant.WHITE}
            />
          )}
          {isInvited && !subUserActive && !isAccessRevoked && (
            <Tag
              icon={<SuccessCircleIcon />}
              title={messagesLocale['INVITED']}
              variant={TagVariant.GRAY}
            />
          )}
        </div>
      ),
      [Column.ACTION]: (
        <Actions
          actions={[
            {
              callback: () => handleView(id),
              title: messagesLocale['VIEW_DETAILS'],
              icon: <EyeIconSmall />,
            },
            (role !== StakeholderRole.ADMIN || roleInCompany === StakeholderRole.ADMIN) &&
            !isTerminated
              ? {
                  callback: () => handleEdit(id),
                  title: messagesLocale['EDIT_DETAILS'],
                  icon: <EditIcon />,
                  attributes: {
                    'data-permission-required': true,
                  },
                }
              : null,
            isInvited && !subUserActive
              ? {
                  callback: () => handleResendInvitation(id, subEmail),
                  title: messagesLocale['RESEND_INVITATION'],
                  icon: <SendInviteIcon />,
                  attributes: {
                    'data-permission-required': true,
                  },
                }
              : null,

            isInvited && !subUserActive
              ? {
                  callback: () => handleCancelInvitation(id, subEmail),
                  title: messagesLocale['CANCEL_INVITATION'],
                  icon: <CloseCircleIconSmall />,
                  attributes: {
                    'data-permission-required': true,
                  },
                }
              : null,

            canDelete && accountId !== id && !hasShares
              ? {
                  callback: () => handleDelete(id, subEmail),
                  title: messagesLocale['DELETE'],
                  className: 'text-fireside-500',
                  icon: <TrashBinIcon iconColor="#F04438" />,
                  attributes: {
                    'data-permission-required': true,
                  },
                }
              : null,
          ]}
        />
      ),
    } satisfies RowPayload<Column>;
  });
  const {
    isOpen: isOpenCancelInvitationModal,
    handleDismissModal: handleDismissCancelInvitationModal,
    handleCloseModal: handleCloseCancelInvitationModal,
    handleSuccessModal: handleSuccessCancelInvitationModal,
    handleOpenModal: handleOpenCancelInvitationModal,
  } = useModalState({
    onSuccess: () => handleSendInvitation(id),
    onDismiss: () => handleSendInvitation(id, email),
  });

  return (
    <>
      <InviteSubEmailsModal
        isOpen={isOpenCancelInvitationModal}
        onDismiss={handleDismissCancelInvitationModal}
        onHardClose={handleCloseCancelInvitationModal}
        onSuccess={handleSuccessCancelInvitationModal}
      />

      <TableRow
        columnOrder={columnOrderMap}
        contentClassName={{
          [Column.CHECK_BOX]: 'justify-center',
          [Column.ACTION]: 'justify-center',
        }}
        fields={payload}
        labelClassName={{
          [Column.CHECK_BOX]: 'w-[50px]',
          [Column.ACTION]: 'w-[50px]',
          [Column.FULL_NAME]: 'w-[200px] h-full',
          [Column.EMAIL]: 'w-[200px]',
          [Column.TYPE]: 'w-[200px]',
          general: 'text-sm font-[450] text-gray-700',
        }}
        trClassName={className}
      />
      {isExpanded &&
        subEmailsPayload?.map((sub) => (
          <TableRow
            columnOrder={columnOrderMap}
            contentClassName={{
              [Column.CHECK_BOX]: 'justify-center',
              [Column.ACTION]: 'justify-center',
            }}
            fields={sub}
            key={sub.id}
            labelClassName={{
              [Column.CHECK_BOX]: 'w-[50px]',
              [Column.ACTION]: 'w-[50px]',
              [Column.FULL_NAME]: 'w-[200px] h-full',
              [Column.EMAIL]: 'w-[200px]',
              [Column.TYPE]: 'w-[200px]',
              general: 'text-sm font-[450] text-gray-700',
            }}
            trClassName={className}
          />
        ))}
    </>
  );
};

export default StakeholderTableRow;
